import type { Chart } from 'chart.js';
import { useCallback, useState } from 'react';
import { HtmlLegend } from '@/components/UI/Charts/HtmlLegend';
import { createHtmlTooltipPlugin } from '@/components/UI/Charts/PieChart/htmlTooltipPlugin';
import { NumberAbbreviation } from '@/components/UI/NumberFormatDisplay/NumberAbbreviation';
import { useDashboardData } from '@/hooks/ReactQueryHooks/useDashboardQuery';
import type { DashboardType } from '@/types/Analytics.ts';
import { transformProjectTypeChartDataSets } from '@/utils';
import Card from '../../UI/Card/index.tsx';
import PieChart from '../../UI/Charts/PieChart';
import { Widget } from '../constants';
import WidgetLayout from '../WidgetLayout';

export interface ConstructionTypeWidgetDataResponse {
  constructionType: string;
  _count: number;
}

export default function ConstructionTypeWidget({ dashboard }: { dashboard: DashboardType }) {
  const fetchConstructionTypeWidgetData = useDashboardData({
    dashboard,
    widget: Widget.PROJECTS_BY_CONSTRUCTION_TYPE,
  });

  const [pieChart, setPieChart] = useState<Chart<'pie'> | null>(null);
  const [htmlTooltipPlugin] = useState(() => createHtmlTooltipPlugin());

  const handlePieChart = useCallback((node: Chart<'pie'>) => {
    setPieChart(node);
  }, []);

  const constructionTypeWidgetData = fetchConstructionTypeWidgetData?.data?.data;
  const constructionTypeLabels = constructionTypeWidgetData?.map((data: ConstructionTypeWidgetDataResponse) => data?.constructionType);

  const projectsByConstructionType = constructionTypeWidgetData?.map((data: ConstructionTypeWidgetDataResponse) => ({
    type: data?.constructionType,
    count: data?._count,
  }));

  const totalConstructionType = constructionTypeWidgetData?.reduce(
    (acc: number, data: ConstructionTypeWidgetDataResponse) => acc + data?._count,
    0
  );

  return (
    <WidgetLayout dashboard={dashboard} widget={Widget.PROJECTS_BY_CONSTRUCTION_TYPE}>
      <Card title='Projects By Construction Type' error={fetchConstructionTypeWidgetData.isError}>
        {constructionTypeLabels?.length > 0 ? (
          <div className='flex flex-col p-5 max-w-screen-lg md:max-w-screen-xs'>
            <div className='relative h-96'>
              <div className='px-5 text-start'>
                <h3 className='text-gray-500 font-bold'>Total Projects</h3>
                <h1 className='m-0'>
                  <NumberAbbreviation value={totalConstructionType} prefix='' />
                </h1>
              </div>
              <div className='md:w-7/8 xl:w-full h-full pt-4 p-0 xl:p-5'>
                <div className='flex h-full w-full flex-col gap-6 items-center justify-center xl:flex-row xl:items-center'>
                  <div className='flex flex-5 min-w-0 pb-2 items-center justify-center overflow-visible'>
                    <PieChart
                      maintainAspectRatio
                      ref={handlePieChart}
                      displayLegend={false}
                      tooltipPlugin={htmlTooltipPlugin}
                      labels={constructionTypeLabels}
                      datasets={transformProjectTypeChartDataSets(projectsByConstructionType)}
                      chartTitle='Total Construction Types'
                      legendAlign='bottom'
                      legendPosition=''
                      showSlicePercentage
                      //containerClassName='h-[320px]'
                    />
                  </div>
                  <HtmlLegend chartInstance={pieChart} className='flex-2 w-full max-w-[220px] shrink-0' />
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className='flex justify-center items-center h-2/3 p-5'>
            <h2>There are no projects by construction type.</h2>
          </div>
        )}
      </Card>
    </WidgetLayout>
  );
}
